import { apiFetch } from "../api"

export type {
    OrderingPropertyMeta,
    OrderingPropertiesForMarketResponse,
    OrderingPropertyType,
    OrderingPropertyValueHint,
} from "./orderingPropertyTypes"
export {
    getOrderingPropertiesForMarket,
    normalizeOrderingPropertiesResponse,
} from "./orderingPropertiesApi"
export {
    buildRankingPromptSnippet,
    formatPropertyTypeLabel,
    formatValueHintSummary,
    isEnumHint,
    type NumericFilterOp,
    type RankingPromptInsertOptions,
} from "./rankingPropertyPrompts"

/** One ranking row from GET /chat/list_rankings_per_market */
export interface RankingPerMarketItem {
    market: string
    id: string
    rankingText: string
    cypherQuery: string
    createdAt: string
    updatedAt: string
    createdBy: string
    lastEditedBy: string
    lastEditedByName: string
}

export interface ListRankingsPerMarketResponse {
    rankings: RankingPerMarketItem[]
}

/**
 * GET /chat/list_rankings_per_market — any authenticated user.
 * @param market optional filter (e.g. FUNDOS)
 */
export const listRankingsPerMarket = async (market?: string): Promise<ListRankingsPerMarketResponse> => {
    const qs = market ? `?market=${encodeURIComponent(market)}` : ""
    const raw = (await apiFetch(`/chat/list_rankings_per_market${qs}`)) as Record<string, unknown>
    const list = Array.isArray(raw.rankings) ? raw.rankings : []
    const rankings: RankingPerMarketItem[] = list
        .map((row) => normalizeRankingRow(row as Record<string, unknown>))
        .filter((r): r is RankingPerMarketItem => r !== null)
    return { rankings }
}

function normalizeRankingRow(row: Record<string, unknown>): RankingPerMarketItem | null {
    const market = String(row.market ?? "").trim()
    if (!market) return null
    const rankingText = String(row.rankingText ?? row.ranking_text ?? "").trim()
    const cypherQuery = String(row.cypherQuery ?? row.cypher_query ?? "").trim()
    return {
        market,
        id: String(row.id ?? ""),
        rankingText,
        cypherQuery,
        createdAt: String(row.createdAt ?? row.created_at ?? ""),
        updatedAt: String(row.updatedAt ?? row.updated_at ?? ""),
        createdBy: String(row.createdBy ?? row.created_by ?? ""),
        lastEditedBy: String(row.lastEditedBy ?? row.last_edited_by ?? ""),
        lastEditedByName: String(row.lastEditedByName ?? row.last_edited_by_name ?? ""),
    }
}

export interface SetSortingProductsResponse {
    /** JSON stringificado com lista de estratégias e produtos. */
    response_json: string
    /** Resposta em markdown para exibir na mensagem do chat. */
    natural_language_response: string
    /** Query Cypher utilizada (opcional, para feedback). */
    cypher_query?: string
    /** 0 = cancel/validation failed, 1 = waiting confirm/cancel, 3 = user confirmed ordering. */
    next_step?: number
    /** Human-readable criterion the user confirmed (e.g. "maior retorno"). Used for display for this market when next_step === 3. */
    ranking_text?: string
}

/**
 * Payload for set_sorting_products.
 * First message: { market, ranking_text }.
 * Follow-up (e.g. confirm): { market, step, user_reply, ranking_text?, cypher_query? }.
 */
export interface SetSortingProductsPayload {
    market: string
    ranking_text?: string
    step?: number
    user_reply?: string
    cypher_query?: string
}

/**
 * Envia a ordenação de produtos por mercado.
 * POST /chat/set_sorting_products
 * Body: market + (ranking_text for step 0) or (step, user_reply, ranking_text?, cypher_query? for step 1).
 */
/** Chave usada no localStorage para persistir o cypher_query retornado por set_sorting_products. */
export const RANKING_IN_USE_STORAGE_KEY = "rankingInUse"

export const setSortingProducts = async (
    payload: SetSortingProductsPayload
): Promise<SetSortingProductsResponse> => {
    const body: Record<string, unknown> = { market: payload.market }
    if (payload.step !== undefined) body.step = payload.step
    if (payload.ranking_text !== undefined) body.ranking_text = payload.ranking_text
    if (payload.user_reply !== undefined) body.user_reply = payload.user_reply
    if (payload.cypher_query !== undefined) body.cypher_query = payload.cypher_query

    const data = (await apiFetch("/chat/set_sorting_products", {
        method: "POST",
        body: JSON.stringify(body),
    })) as SetSortingProductsResponse

    if (data?.next_step === 3 && data.cypher_query && typeof localStorage !== "undefined") {
        try {
            localStorage.setItem(RANKING_IN_USE_STORAGE_KEY, data.cypher_query)
        } catch {
            // ignorar falhas (ex.: modo anônimo, quota)
        }
    }

    return data
}
